/**
 * Turn a work-order API refusal into translated copy (`workOrders:errors.<CODE>`); the engine's
 * ACTION_BLOCKED names its failed conditions (`workOrders:guards.<KEY>`). Anything else falls back to a
 * generic line with the technical summary.
 */

import { useTranslation } from 'react-i18next';

import { describeApiError, technicalSummary } from '@/shared/api/error';

export function useWorkOrderError(): (error: unknown) => string {
  const { t, i18n } = useTranslation('workOrders');
  return (error: unknown): string => {
    const info = describeApiError(error);
    if (info.isNetworkError) return t('errors.network');
    if (info.code === 'ACTION_BLOCKED') {
      const guards = ((info.details?.failedGuards as Array<{ guard: string }> | undefined) ?? []).map((g) =>
        i18n.exists(`workOrders:guards.${g.guard}`) ? t(`guards.${g.guard}`) : g.guard,
      );
      return t('errors.ACTION_BLOCKED', { guards: guards.join('; ') });
    }
    if (info.code !== undefined && i18n.exists(`workOrders:errors.${info.code}`)) {
      return t(`errors.${info.code}`);
    }
    const tech = technicalSummary(info);
    return tech === '' ? t('errors.generic') : `${t('errors.generic')} (${tech})`;
  };
}
